import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

const addBtnCls =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const delBtnCls = "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44]";
const moveBtnCls = "bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs";

/** Editor for the public "Activity" page: events, clubs, achievements. */
export default function ActivityContentPage() {
  const [sub, setSub] = useState("");
  const [events, setEvents] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const d = (await adminGet("/admin/content/activity")) || {};
      setSub(d.hero?.sub || "");
      setEvents(d.events || []);
      setClubs(d.clubs || []);
      setAchievements(d.achievements || []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const upd = (setter) => (i, f, v) =>
    setter((arr) => arr.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)));
  const move = (setter) => (i, dir) =>
    setter((arr) => {
      const j = i + dir;
      if (j < 0 || j >= arr.length) return arr;
      const c = [...arr];
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  const del = (setter) => (i) => setter((arr) => arr.filter((_, idx) => idx !== i));

  const updEv = upd(setEvents), moveEv = move(setEvents), delEv = del(setEvents);
  const updCl = upd(setClubs), moveCl = move(setClubs), delCl = del(setClubs);
  const updAc = upd(setAchievements), moveAc = move(setAchievements), delAc = del(setAchievements);

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const payload = {
        hero: { sub: sub.trim() },
        events: events.filter((e) => (e.title || "").trim()),
        clubs: clubs.filter((c) => (c.name || "").trim()),
        achievements: achievements.filter((a) => (a.title || "").trim()),
      };
      const saved = await adminPut("/admin/content/activity", payload);
      if (saved) {
        setSub(saved.hero?.sub || "");
        setEvents(saved.events || []);
        setClubs(saved.clubs || []);
        setAchievements(saved.achievements || []);
      }
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}
      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      {/* Hero subtitle */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead title="Page Subtitle" />
        <div className="p-[22px]">
          <input
            value={sub}
            onChange={(e) => setSub(e.target.value)}
            placeholder="Short line under the page title"
            className={inputCls}
          />
        </div>
      </AdminCard>

      {/* Events */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Upcoming Events"
          action={
            <button
              onClick={() =>
                setEvents([...events, { date: "", title: "", venue: "", time: "", desc: "" }])
              }
              className={addBtnCls}
            >
              + Add Event
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {events.length === 0 && <div className="text-muted text-sm">No events yet.</div>}
          {events.map((ev, i) => (
            <div
              key={i}
              className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[130px_1fr_auto] gap-3 items-start"
            >
              <div className="flex flex-col gap-2">
                <input value={ev.date || ""} onChange={(e) => updEv(i, "date", e.target.value)} placeholder="15 Jan 2027" className={inputCls} />
                <input value={ev.time || ""} onChange={(e) => updEv(i, "time", e.target.value)} placeholder="9:00 AM" className={inputCls} />
              </div>
              <div className="flex flex-col gap-2">
                <input value={ev.title || ""} onChange={(e) => updEv(i, "title", e.target.value)} placeholder="Event title" className={inputCls} />
                <input value={ev.venue || ""} onChange={(e) => updEv(i, "venue", e.target.value)} placeholder="Venue" className={inputCls} />
                <textarea value={ev.desc || ""} onChange={(e) => updEv(i, "desc", e.target.value)} placeholder="One-line description" rows={2} className={`${inputCls} resize-y`} />
              </div>
              <div className="flex md:flex-col gap-1.5">
                <button onClick={() => moveEv(i, -1)} title="Up" className={moveBtnCls}>↑</button>
                <button onClick={() => moveEv(i, 1)} title="Down" className={moveBtnCls}>↓</button>
                <button onClick={() => delEv(i)} title="Remove" className={delBtnCls}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Clubs */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Clubs & Co-curricular"
          action={
            <button
              onClick={() => setClubs([...clubs, { icon: "⭐", name: "", desc: "", color: "#005C40" }])}
              className={addBtnCls}
            >
              + Add Club
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {clubs.length === 0 && <div className="text-muted text-sm">No clubs yet.</div>}
          {clubs.map((c, i) => (
            <div
              key={i}
              className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[60px_1fr_90px_auto] gap-3 items-start"
            >
              <input value={c.icon || ""} onChange={(e) => updCl(i, "icon", e.target.value)} placeholder="🎤" className={`${inputCls} text-center`} />
              <div className="flex flex-col gap-2">
                <input value={c.name || ""} onChange={(e) => updCl(i, "name", e.target.value)} placeholder="Club name" className={inputCls} />
                <textarea value={c.desc || ""} onChange={(e) => updCl(i, "desc", e.target.value)} placeholder="What the club does" rows={2} className={`${inputCls} resize-y`} />
              </div>
              <input type="color" value={c.color || "#005C40"} onChange={(e) => updCl(i, "color", e.target.value)} title="Accent colour" className="w-full h-10 border border-line rounded-lg cursor-pointer" />
              <div className="flex md:flex-col gap-1.5">
                <button onClick={() => moveCl(i, -1)} title="Up" className={moveBtnCls}>↑</button>
                <button onClick={() => moveCl(i, 1)} title="Down" className={moveBtnCls}>↓</button>
                <button onClick={() => delCl(i)} title="Remove" className={delBtnCls}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      {/* Achievements */}
      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Recent Achievements"
          action={
            <button
              onClick={() => setAchievements([...achievements, { year: "", title: "", detail: "" }])}
              className={addBtnCls}
            >
              + Add Achievement
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {achievements.length === 0 && <div className="text-muted text-sm">None yet.</div>}
          {achievements.map((a, i) => (
            <div
              key={i}
              className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[90px_1fr_auto] gap-3 items-start"
            >
              <input value={a.year || ""} onChange={(e) => updAc(i, "year", e.target.value)} placeholder="2026" className={inputCls} />
              <div className="flex flex-col gap-2">
                <input value={a.title || ""} onChange={(e) => updAc(i, "title", e.target.value)} placeholder="Achievement" className={inputCls} />
                <textarea value={a.detail || ""} onChange={(e) => updAc(i, "detail", e.target.value)} placeholder="Details" rows={2} className={`${inputCls} resize-y`} />
              </div>
              <div className="flex md:flex-col gap-1.5">
                <button onClick={() => moveAc(i, -1)} title="Up" className={moveBtnCls}>↑</button>
                <button onClick={() => moveAc(i, 1)} title="Down" className={moveBtnCls}>↓</button>
                <button onClick={() => delAc(i)} title="Remove" className={delBtnCls}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
